import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { hojeBR } from '../utils/data';
import useRecorrenciaHabilitada from '../hooks/useRecorrenciaHabilitada';
import { fmtMoeda, fmtData, rotuloDia, situacaoDoMes } from '../components/clientes/recorrenciaUtil';
import s from './CadastroClientes.module.css';

/* Vendas → Gerar Recorrência — junta as recorrências configuradas em cada
   cliente (Cadastro de Clientes → aba Recorrência). Antes de confirmar, o
   usuário marca/desmarca linha a linha: incluir, gerar boleto, gerar NFS-e e
   enviar e-mail (vale só para esta geração; o padrão fica no cliente). */

const COLUNAS = [
  { campo: 'gerar_boleto', label: 'Gerar boleto' },
  { campo: 'gerar_nfse',   label: 'Gerar NFS-e' },
  { campo: 'enviar_email', label: 'Enviar e-mail' },
];
const COR_STATUS = { ok: '#1b7a3d', aviso: '#e65100', erro: '#c62828', ignorado: '#6b7280' };

const jaGerada = r => r.venda_id && r.venda_status !== 'cancelada';

export default function GerarRecorrencia() {
  const qc = useQueryClient();
  const habilitada = useRecorrenciaHabilitada();
  const [competencia, setCompetencia] = useState(() => hojeBR().slice(0, 7));
  const [escolhas, setEscolhas] = useState({});
  const [ocupado, setOcupado] = useState('');
  const [erro, setErro] = useState('');
  const [resultados, setResultados] = useState(null);
  const [ano, mes] = competencia.split('-');

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['recorrencias', 'competencia', competencia],
    queryFn: () => api.get('/recorrencias', { params: { competencia } }).then(r => r.data),
    enabled: habilitada,
  });
  const linhas = useMemo(() => (data?.recorrencias || []).filter(r => r.ativo), [data]);

  // Marcações iniciais = configuração salva no cliente; linhas já geradas no
  // mês ficam de fora (não dá pra gerar duas vezes). Um recarregamento da
  // lista (foco na janela, ação de outra linha) mantém o que o usuário já
  // marcou/desmarcou; trocar de competência recomeça do padrão.
  useEffect(() => { setEscolhas({}); }, [competencia]);
  useEffect(() => {
    setEscolhas(prev => {
      const n = {};
      for (const r of linhas) {
        n[r.id] = prev[r.id] ? { ...prev[r.id] } : {
          incluir: true, gerar_boleto: r.gerar_boleto, gerar_nfse: r.gerar_nfse, enviar_email: r.enviar_email,
        };
        if (jaGerada(r)) n[r.id].incluir = false;
      }
      return n;
    });
  }, [linhas]);

  const pendentes = linhas.filter(r => !jaGerada(r));
  const marcadas = pendentes.filter(r => escolhas[r.id]?.incluir);
  const totalMarcado = marcadas.reduce((t, r) => t + Number(r.valor || 0), 0);

  const alternar = (id, campo) => setEscolhas(e => ({ ...e, [id]: { ...e[id], [campo]: !e[id]?.[campo] } }));
  const alternarColuna = (campo) => {
    const alvo = campo === 'incluir' ? pendentes : marcadas;
    const todosMarcados = alvo.length > 0 && alvo.every(r => escolhas[r.id]?.[campo]);
    setEscolhas(e => {
      const n = { ...e };
      for (const r of alvo) n[r.id] = { ...n[r.id], [campo]: !todosMarcados };
      return n;
    });
  };
  const colunaMarcada = (campo) => {
    const alvo = campo === 'incluir' ? pendentes : marcadas;
    return alvo.length > 0 && alvo.every(r => escolhas[r.id]?.[campo]);
  };

  const atualizar = () => qc.invalidateQueries({ queryKey: ['recorrencias'] });

  const gerar = async () => {
    if (!marcadas.length) return;
    const conta = campo => marcadas.filter(r => escolhas[r.id]?.[campo]).length;
    const msg = `Gerar ${marcadas.length} venda(s) de ${mes}/${ano} (total ${fmtMoeda(totalMarcado)}), finalizadas em boleto?\n\n` +
      `• Boleto emitido no banco: ${conta('gerar_boleto')}\n• NFS-e: ${conta('gerar_nfse')}\n• E-mail ao cliente: ${conta('enviar_email')}`;
    if (!window.confirm(msg)) return;
    setErro(''); setResultados(null); setOcupado('gerar');
    try {
      const itens = marcadas.map(r => ({ id: r.id, ...escolhas[r.id] }));
      const { data: resp } = await api.post('/recorrencias/gerar', { competencia, itens });
      setResultados({ titulo: 'Gerar recorrência', itens: resp.resultados || [] });
    } catch (e) {
      setErro(e.response?.data?.erro || 'Falha ao gerar a recorrência.');
    } finally {
      setOcupado(''); atualizar();
    }
  };

  // Vendas geradas cujo boleto falhou (ex.: banco recusou por excesso de chamadas).
  const semBoleto = linhas.filter(r => jaGerada(r) && /boleto/i.test(r.aviso || ''));
  const gerarBoletosFaltando = async () => {
    if (!window.confirm(`Gerar agora o boleto de ${semBoleto.length} venda(s) de ${mes}/${ano} que ficaram sem boleto?\n\nUm por vez, com pausa, para o banco não recusar de novo.`)) return;
    setErro(''); setResultados(null); setOcupado('boletos');
    try {
      const { data: resp } = await api.post('/recorrencias/boletos', { competencia });
      setResultados({ titulo: 'Gerar boletos que faltaram', itens: resp.resultados || [] });
    } catch (e) {
      setErro(e.response?.data?.erro || 'Falha ao gerar os boletos.');
    } finally {
      setOcupado(''); atualizar();
    }
  };

  const refazer = async (acao, r, titulo) => {
    setErro(''); setResultados(null); setOcupado(`${acao}-${r.id}`);
    try {
      const { data: resp } = await api.post(`/recorrencias/${acao}`, { competencia, ids: [r.id] });
      setResultados({ titulo, itens: resp.resultados || [] });
    } catch (e) {
      setErro(e.response?.data?.erro || 'Falha ao executar.');
    } finally {
      setOcupado(''); atualizar();
    }
  };

  if (!habilitada) {
    return (
      <div className={s.erpWrap}>
        <div className={s.titleBar}>🔁 Gerar Recorrência</div>
        <div className={s.tabContent}>
          <div className={s.abaPlaceholder}>Recorrência não habilitada — ligue em Minha Empresa → Parâmetros → Vendas.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}>🔁 Gerar Recorrência</div>
      <div className={s.tabContent} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
          <label>Competência:</label>
          <input type="month" className={s.inputErp} style={{ width: 200 }} value={competencia}
            onChange={e => e.target.value && setCompetencia(e.target.value)} />
          <span style={{ color: '#555' }}>
            Configure serviço, valor e vencimento de cada cliente em Cad. Clientes → aba Recorrência.
          </span>
          <span style={{ flex: 1 }} />
          {semBoleto.length > 0 && (
            <button className={s.btnErp} disabled={!!ocupado} onClick={gerarBoletosFaltando}
              title="Emite o boleto das vendas já geradas que ficaram sem boleto">
              {ocupado === 'boletos' ? 'Gerando boletos...' : `🔁 Gerar boletos que faltaram (${semBoleto.length})`}
            </button>
          )}
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={!!ocupado || !marcadas.length} onClick={gerar}>
            {ocupado === 'gerar' ? 'Gerando...' : `Gerar recorrência (${marcadas.length})`}
          </button>
        </div>

        {erro && <div style={{ color: '#c62828', fontSize: 13 }}>{erro}</div>}

        {isError ? (
          <div className={s.abaPlaceholder}>{error?.response?.data?.erro || 'Não foi possível carregar as recorrências.'}</div>
        ) : (
          <div style={{ overflow: 'auto', flex: 1, minHeight: 200 }}>
            <table className={s.modalTabela}>
              <thead>
                <tr>
                  <th style={{ width: 54 }} title="Marcar/desmarcar todas">
                    <input type="checkbox" checked={colunaMarcada('incluir')} onChange={() => alternarColuna('incluir')} /> Gerar
                  </th>
                  <th>Cliente</th>
                  <th>Serviço</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Valor</th>
                  <th style={{ width: 110 }}>Vencimento</th>
                  <th style={{ width: 170 }}>Banco do boleto</th>
                  {COLUNAS.map(c => (
                    <th key={c.campo} style={{ width: 100, minWidth: 100, whiteSpace: 'normal' }} title="Marcar/desmarcar nas linhas marcadas">
                      <input type="checkbox" checked={colunaMarcada(c.campo)} onChange={() => alternarColuna(c.campo)} /> {c.label}
                    </th>
                  ))}
                  <th>Situação em {mes}/{ano}</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map(r => {
                  const gerada = jaGerada(r);
                  const esc = escolhas[r.id] || {};
                  const sit = situacaoDoMes(r);
                  return (
                    <tr key={r.id} style={{ background: gerada ? '#f7f7f7' : undefined }}>
                      <td style={{ cursor: 'default', textAlign: 'center' }}>
                        <input type="checkbox" disabled={gerada} checked={!gerada && !!esc.incluir}
                          onChange={() => alternar(r.id, 'incluir')} />
                      </td>
                      <td style={{ cursor: 'default' }}>
                        {r.cliente_nome}
                        {!r.cliente_email && esc.enviar_email && !gerada && (
                          <div style={{ fontSize: 11, color: '#e65100' }}>sem e-mail no cadastro</div>
                        )}
                      </td>
                      <td style={{ cursor: 'default' }}>{r.produto_nome}</td>
                      <td style={{ cursor: 'default', textAlign: 'right' }}>{fmtMoeda(r.valor)}</td>
                      <td style={{ cursor: 'default' }} title={r.gerada_vencimento ? `Vencimento ${fmtData(r.gerada_vencimento)}` : ''}>
                        {rotuloDia(r.dia_vencimento)}
                      </td>
                      <td style={{ cursor: 'default' }}>{r.conta_nome || 'Conta padrão da loja'}</td>
                      {COLUNAS.map(c => (
                        <td key={c.campo} style={{ cursor: 'default', textAlign: 'center' }}>
                          <input type="checkbox" disabled={gerada || !esc.incluir} checked={!!esc[c.campo]}
                            onChange={() => alternar(r.id, c.campo)} />
                        </td>
                      ))}
                      <td style={{ cursor: 'default', fontSize: 12 }}>
                        <span style={{ color: sit.cor }} title={sit.dica}>{sit.texto}</span>
                        {gerada && /boleto/i.test(r.aviso || '') && (
                          <> <button className={s.btnErp} disabled={!!ocupado} title={r.aviso}
                            onClick={() => refazer('boletos', r, 'Gerar boleto')}>Boleto</button></>
                        )}
                        {gerada && r.nfse_status !== 'autorizada' && (
                          <> <button className={s.btnErp} disabled={!!ocupado} title="Emitir a NFS-e desta venda"
                            onClick={() => refazer('nfse', r, 'Emitir NFS-e')}>NFS-e</button></>
                        )}
                        {gerada && (
                          <> <button className={s.btnErp} disabled={!!ocupado} title="Enviar boleto e nota para o e-mail do cliente"
                            onClick={() => refazer('email', r, 'Enviar e-mail')}>✉</button></>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!linhas.length && (
                  <tr><td colSpan={10} style={{ cursor: 'default', textAlign: 'center', color: '#6b7280', padding: 18 }}>
                    {isFetching ? 'Carregando...' : 'Nenhuma recorrência ativa. Configure em Cad. Clientes → aba Recorrência.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', gap: 24, justifyContent: 'flex-end', fontSize: 13, fontWeight: 600 }}>
          <span>Marcadas: {marcadas.length} — {fmtMoeda(totalMarcado)}</span>
          <span>Já geradas em {mes}/{ano}: {linhas.length - pendentes.length}</span>
        </div>

        {resultados && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, fontSize: 12.5, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <strong>{resultados.titulo} — {mes}/{ano}</strong>
              <button className={s.btnErp} onClick={() => setResultados(null)}>Fechar</button>
            </div>
            {!resultados.itens.length ? 'Nada para processar.' : resultados.itens.map((x, i) => (
              <div key={i} style={{ color: COR_STATUS[x.status] || undefined }}>
                <strong>{x.cliente}</strong>: {x.mensagem}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
